/**
 * Helpers that turn a FILE / IMAGE EAV attribute value into EvidenceFileList
 * rows. Kept out of the React component file so react-refresh stays happy.
 */

import type { EvidenceFileListItem } from '@/shared/components/evidence/evidence-file-list'

type FileEnvelopeInner = {
  uuid?: unknown
  file_name?: unknown
  fileName?: unknown
  file_url?: unknown
  fileUrl?: unknown
  thumbnail_url?: unknown
  thumbnailUrl?: unknown
  mime_type?: unknown
  mimeType?: unknown
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function unwrapEnvelope(value: unknown): FileEnvelopeInner | null {
  const record = asRecord(value)
  if (!record) return null
  const nested = asRecord(record.value)
  if (nested && (nested.uuid || nested.file_url || nested.fileUrl || nested.file_name)) {
    return nested as FileEnvelopeInner
  }
  if (record.uuid || record.file_url || record.fileUrl || record.file_name) {
    return record as FileEnvelopeInner
  }
  return null
}

function toListItem(value: unknown, index: number): EvidenceFileListItem | null {
  if (typeof value === 'string' && value.trim()) {
    const uuid = value.trim()
    return {
      key: uuid || `file-${index}`,
      // Never surface the bare UUID as the row label - EvidenceFileRow also
      // humanises UUID-stem names, but this path has no real filename at all.
      fileName: 'Uploaded file',
      fileUuid: uuid,
      fileUrl: null,
      thumbnailUrl: null,
    }
  }
  const inner = unwrapEnvelope(value)
  if (!inner) return null
  const uuid = typeof inner.uuid === 'string' ? inner.uuid : ''
  const fileName =
    [inner.file_name, inner.fileName].find(
      (v): v is string => typeof v === 'string' && v.trim().length > 0,
    ) ||
    (asRecord(value)?.display as string | undefined) ||
    uuid ||
    'Uploaded file'
  const fileUrl =
    (typeof inner.file_url === 'string' && inner.file_url) ||
    (typeof inner.fileUrl === 'string' && inner.fileUrl) ||
    null
  const thumbnailUrl =
    (typeof inner.thumbnail_url === 'string' && inner.thumbnail_url) ||
    (typeof inner.thumbnailUrl === 'string' && inner.thumbnailUrl) ||
    fileUrl
  const mimeType =
    (typeof inner.mime_type === 'string' && inner.mime_type) ||
    (typeof inner.mimeType === 'string' && inner.mimeType) ||
    null
  if (!uuid && !fileUrl) return null
  return {
    key: uuid || `file-${index}`,
    fileName,
    fileUuid: uuid || null,
    fileUrl,
    thumbnailUrl,
    mimeType,
  }
}

/** Normalise a stored file attribute value into EvidenceFileList rows. */
export function fileAttributeToEvidenceItems(
  value: unknown,
): EvidenceFileListItem[] {
  if (value === undefined || value === null || value === '') return []
  const raw = Array.isArray(value) ? value : [value]
  return raw
    .map((item, index) => toListItem(item, index))
    .filter((item): item is EvidenceFileListItem => item !== null)
}

/** True when this field type should render via AttributeFileGallery. */
export function isFileAttributeFieldType(fieldType: string | undefined): boolean {
  if (!fieldType) return false
  const upper = fieldType.toUpperCase()
  return (
    upper === 'FILE' ||
    upper === 'IMAGE' ||
    upper === 'DOCUMENT' ||
    upper === 'VIDEO'
  )
}
